import { Pill, CalendarCheck, ArrowRightLeft, FileUp, FlaskConical, Voicemail, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToSection } from "@/lib/scroll";

const SERVICES = [
  {
    title: "Prior Authorization — Medicines, Radiology & Infusion",
    tag: "Revenue",
    icon: Pill,
    dark: true,
    desc: "Medication, imaging, and infusion authorizations submitted, tracked, and followed up before the appointment — no more delayed care or denied-claim surprises.",
  },
  {
    title: "Patient Calling & Scheduling",
    tag: "Front Desk",
    icon: CalendarCheck,
    dark: false,
    desc: "Inbound and outbound patient calls, appointment booking, recall reminders, and no-show recovery — handled in your EHR, in your time zone.",
  },
  {
    title: "Referral Management",
    tag: "Care Coordination",
    icon: ArrowRightLeft,
    dark: false,
    desc: "Outgoing referrals sent, incoming referrals triaged, and specialist appointments confirmed — with patients kept informed at every step.",
  },
  {
    title: "Fax Uploading & Records",
    tag: "Documents",
    icon: FileUp,
    dark: false,
    desc: "Inbound faxes sorted, uploaded, and filed to the right patient chart the same day — your document queue never piles up.",
  },
  {
    title: "Lab Ordering",
    tag: "Clinical",
    icon: FlaskConical,
    dark: false,
    desc: "Lab orders entered accurately, results routed to providers, and patients notified — a tight loop from order to follow-up.",
  },
  {
    title: "Voicemail Assistance",
    tag: "Inbox",
    icon: Voicemail,
    dark: false,
    desc: "Every voicemail transcribed, triaged, and actioned each morning — urgent messages escalated, routine ones resolved before lunch.",
  },
];

export const Services = () => (
  <section id="services" className="py-20 lg:py-28 bg-[#F3F2EB]/60" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow>What Your VMA Handles</Eyebrow>
          <h2 className="mt-4 max-w-xl font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-[#0F1E19]">
            One assistant. Your entire back office.
          </h2>
        </div>
        <button
          data-testid="services-consult-btn"
          onClick={() => scrollToSection("#contact")}
          className="group inline-flex items-center gap-2 text-sm font-semibold text-[#0A3B2C]"
        >
          Discuss your workflow
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
      </Reveal>

      <div className="mt-12 grid gap-5 md:grid-cols-6" data-testid="services-bento">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <Reveal key={s.title} delay={i * 0.08} className="md:col-span-2">
              <div
                data-testid={`service-card-${i}`}
                className={`group h-full rounded-3xl border p-7 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl ${
                  s.dark
                    ? "border-[#0A261E] bg-[#0A261E]"
                    : "border-[#E2E0D5] bg-white hover:border-[#0A3B2C]/30"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div
                    className={`flex h-11 w-11 items-center justify-center rounded-xl ${
                      s.dark ? "bg-[#123328]" : "bg-[#E6F4EE]"
                    }`}
                  >
                    <Icon className={`h-5 w-5 ${s.dark ? "text-[#EAB308]" : "text-[#0A3B2C]"}`} />
                  </div>
                  <span
                    className={`font-mono text-[10px] uppercase tracking-[0.2em] ${
                      s.dark ? "text-[#A3B8B0]" : "text-[#4A5D55]"
                    }`}
                  >
                    {s.tag}
                  </span>
                </div>
                <h3
                  className={`mt-6 font-display text-xl font-semibold leading-snug ${
                    s.dark ? "text-[#F5F7F6]" : "text-[#0F1E19]"
                  }`}
                >
                  {s.title}
                </h3>
                <p className={`mt-3 text-sm leading-relaxed ${s.dark ? "text-[#A3B8B0]" : "text-[#4A5D55]"}`}>
                  {s.desc}
                </p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);
